import { useState, useCallback, useEffect } from 'react'
import type {
  Notification,
  NotificationPreferences,
  NotificationType,
} from '../types'

const STORAGE_KEY = 'aerozeep_notifications'
const PREFERENCES_KEY = 'aerozeep_notification_preferences'

const defaultPreferences: NotificationPreferences = {
  email: true,
  sms: true,
  push: true,
  refundUpdates: true,
  withdrawalUpdates: true,
  promoOffers: true,
  adminAlerts: true,
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [preferences, setPreferences] =
    useState<NotificationPreferences>(defaultPreferences)
  const [isLoading, setIsLoading] = useState(false)

  // Load notifications from storage
  const loadNotifications = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setNotifications(JSON.parse(stored))
      }
    } catch (err) {
      console.error('Failed to load notifications', err)
    }
  }, [])

  // Load preferences from storage
  const loadPreferences = useCallback(() => {
    try {
      const stored = localStorage.getItem(PREFERENCES_KEY)
      if (stored) {
        setPreferences(JSON.parse(stored))
      } else {
        localStorage.setItem(
          PREFERENCES_KEY,
          JSON.stringify(defaultPreferences),
        )
      }
    } catch (err) {
      console.error('Failed to load preferences', err)
    }
  }, [])

  // Save notifications to storage
  const saveNotifications = useCallback((newNotifications: Notification[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newNotifications))
      setNotifications(newNotifications)
      window.dispatchEvent(new Event('notifications-updated'))
    } catch (err) {
      console.error('Failed to save notifications', err)
    }
  }, [])

  // Save preferences to storage
  const savePreferences = useCallback(
    (newPreferences: NotificationPreferences) => {
      try {
        localStorage.setItem(PREFERENCES_KEY, JSON.stringify(newPreferences))
        setPreferences(newPreferences)
      } catch (err) {
        console.error('Failed to save preferences', err)
      }
    },
    [],
  )

  // Create a new notification
  const createNotification = useCallback(
    async (
      type: NotificationType,
      title: string,
      message: string,
      metadata?: Record<string, unknown>,
    ): Promise<Notification> => {
      setIsLoading(true)

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 500))

        const newNotification: Notification = {
          id: `notif-${Date.now()}`,
          userId: 'user-001',
          type,
          title,
          message,
          channels: ['in_app', 'email'],
          read: false,
          createdAt: new Date().toISOString(),
          metadata,
        }

        const updated = [newNotification, ...notifications]
        saveNotifications(updated)
        return newNotification
      } finally {
        setIsLoading(false)
      }
    },
    [notifications, saveNotifications],
  )

  // Mark notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      const updated = notifications.map((n) =>
        n.id === notificationId ? { ...n, read: true } : n,
      )
      saveNotifications(updated)
    },
    [notifications, saveNotifications],
  )

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    const updated = notifications.map((n) => ({ ...n, read: true }))
    saveNotifications(updated)
  }, [notifications, saveNotifications])

  // Delete notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      const updated = notifications.filter((n) => n.id !== notificationId)
      saveNotifications(updated)
    },
    [notifications, saveNotifications],
  )

  // Update preferences
  const updatePreferences = useCallback(
    async (newPreferences: Partial<NotificationPreferences>) => {
      const updated = { ...preferences, ...newPreferences }
      savePreferences(updated)
    },
    [preferences, savePreferences],
  )

  // Get unread count
  const getUnreadCount = useCallback(() => {
    return notifications.filter((n) => !n.read).length
  }, [notifications])

  // Initialize on mount
  useEffect(() => {
    loadNotifications()
    loadPreferences()

    const handleUpdate = () => loadNotifications()
    window.addEventListener('notifications-updated', handleUpdate)

    return () => {
      window.removeEventListener('notifications-updated', handleUpdate)
    }
  }, [loadNotifications, loadPreferences])

  return {
    notifications,
    preferences,
    isLoading,
    createNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updatePreferences,
    getUnreadCount,
  }
}
